import React, { ForwardedRef, forwardRef } from 'react';
import clsx from 'clsx';
import {
  SapphireStyleProps,
  useSapphireStyleProps,
  useThemeCheck,
} from '../../utils';
import { GlobalDomAttributes } from '../../utils/types';
import styles from '@danske/sapphire-css/components/buttonGroup/buttonGroup.module.css';
import { filterDOMProps } from 'react-aria/filterDOMProps';
import { DOMProps } from '@react-types/shared';

export type ButtonGroupStretchType = 'none' | 'default' | 'autoVertical';

export interface ButtonGroupProps
  extends GlobalDomAttributes,
    SapphireStyleProps,
    DOMProps {
  /**
   * Alignment of buttons within the group.
   * @default "right"
   */
  align?: 'left' | 'center' | 'right';
  /**
   * Controls stretching of the contents.
   *
   * `none` - the children will not stretch and will respect the alignment set with the align prop.
   *
   * `default` - the children will stretch to take full width. If the children overflow, they will be wrapped vertically.
   *
   * `autoVertical` - same behaviour as `default`, but if there is more than 3 children, they will be wrapped vertically even if they don't overflow.
   *
   * @default "none"
   */
  stretch?: ButtonGroupStretchType;
  children: React.ReactNode;
  /**
   * Spacing between the buttons.
   * @default 'md'
   */
  spacing?: 'lg' | 'md' | 'sm' | 'xs';
  /**
   *
   * @default "horizontal"
   */
  orientation?: 'vertical' | 'horizontal';
}

/**
 * ButtonGroup is a utility component which ensures consistent spacing between buttons
 * All children must be of type Button. Icon buttons cannot be mixed with standard buttons.
 */
export const ButtonGroup = forwardRef(function ButtonGroup(
  props: ButtonGroupProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  useThemeCheck();
  const {
    align = 'right',
    stretch = 'none',
    children,
    spacing = 'md',
    orientation = 'horizontal',
  } = props;
  const { styleProps } = useSapphireStyleProps(props);

  return (
    <div
      className={clsx(styleProps.className, styles['sapphire-button-group'], {
        [styles[`sapphire-button-group--align-${align}` as const]]:
          stretch === 'none',
        [styles[`sapphire-button-group--stretch`]]: stretch !== 'none',
        [styles[`sapphire-button-group--stretch-auto-vertical`]]:
          stretch === 'autoVertical',
        [styles[`sapphire-button-group--spacing-sm`]]: spacing === 'sm',
        [styles[`sapphire-button-group--spacing-lg`]]: spacing === 'lg',
        [styles[`sapphire-button-group--spacing-xs`]]: spacing === 'xs',
        [styles[`sapphire-button-group--vertical`]]: orientation === 'vertical',
      })}
      style={styleProps.style}
      ref={ref}
      {...filterDOMProps(props, { global: true })}
    >
      {children}
    </div>
  );
});
